import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  X,
  Package,
  Utensils,
  Camera,
  Headphones,
  Car,
  Ticket,
  Shirt,
  Smartphone,
  Wifi,
  Wine,
  Music,
  Palette,
  Globe,
  MapPin,
  Clock,
  Users,
  Star,
  Heart,
  Bike,
  Waves,
  Mountain,
  Sun,
  Coffee,
  ShoppingBag,
  Gift,
  Compass,
  Zap,
  Shield,
  CreditCard,
  Bell,
  Map as MapIcon,
  Tent,
  Ship,
  Plane,
  Train,
  Bus,
  Footprints,
  Camera as CameraIcon,
  Mic,
  Languages,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { Modal, TextInput, Button } from '@/components/ui';

export interface IconOption {
  name: string;
  icon: any;
  category: 'travel' | 'activities' | 'services' | 'food' | 'art' | 'general';
}

const ALL_ICONS: IconOption[] = [
  // General
  { name: 'package', icon: Package, category: 'general' },
  { name: 'gift', icon: Gift, category: 'general' },
  { name: 'zap', icon: Zap, category: 'general' },
  { name: 'shield', icon: Shield, category: 'general' },
  { name: 'bell', icon: Bell, category: 'general' },
  { name: 'info', icon: Info, category: 'general' },
  { name: 'check-circle', icon: CheckCircle2, category: 'general' },
  { name: 'star', icon: Star, category: 'general' },
  { name: 'heart', icon: Heart, category: 'general' },

  // Travel
  { name: 'globe', icon: Globe, category: 'travel' },
  { name: 'map-pin', icon: MapPin, category: 'travel' },
  { name: 'map', icon: MapIcon, category: 'travel' },
  { name: 'compass', icon: Compass, category: 'travel' },
  { name: 'mountain', icon: Mountain, category: 'travel' },
  { name: 'sun', icon: Sun, category: 'travel' },
  { name: 'waves', icon: Waves, category: 'travel' },
  { name: 'tent', icon: Tent, category: 'travel' },
  { name: 'footprints', icon: Footprints, category: 'travel' },
  { name: 'plane', icon: Plane, category: 'travel' },
  { name: 'ship', icon: Ship, category: 'travel' },
  { name: 'train', icon: Train, category: 'travel' },
  { name: 'bus', icon: Bus, category: 'travel' },
  { name: 'car', icon: Car, category: 'travel' },

  // Activities
  { name: 'camera', icon: Camera, category: 'activities' },
  { name: 'bike', icon: Bike, category: 'activities' },
  { name: 'headphones', icon: Headphones, category: 'activities' },
  { name: 'users', icon: Users, category: 'activities' },
  { name: 'clock', icon: Clock, category: 'activities' },
  { name: 'ticket', icon: Ticket, category: 'activities' },
  { name: 'mic', icon: Mic, category: 'activities' },

  // Services
  { name: 'wifi', icon: Wifi, category: 'services' },
  { name: 'smartphone', icon: Smartphone, category: 'services' },
  { name: 'shirt', icon: Shirt, category: 'services' },
  { name: 'credit-card', icon: CreditCard, category: 'services' },
  { name: 'languages', icon: Languages, category: 'services' },
  { name: 'shopping-bag', icon: ShoppingBag, category: 'services' },

  // Food & Drink
  { name: 'utensils', icon: Utensils, category: 'food' },
  { name: 'wine', icon: Wine, category: 'food' },
  { name: 'coffee', icon: Coffee, category: 'food' },

  // Art & Culture
  { name: 'music', icon: Music, category: 'art' },
  { name: 'palette', icon: Palette, category: 'art' },
];

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'travel', label: 'Travel' },
  { id: 'activities', label: 'Activities' },
  { id: 'services', label: 'Services' },
  { id: 'food', label: 'Food' },
  { id: 'art', label: 'Art' },
  { id: 'general', label: 'General' },
];

export const ICON_COMPONENTS: Record<string, any> = Object.fromEntries(ALL_ICONS.map((item) => [item.name, item.icon]));

interface IconPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (iconName: string) => void;
  selectedIcon?: string;
}

export function IconPickerModal({ isOpen, onClose, onSelect, selectedIcon }: IconPickerModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredIcons = useMemo(() => {
    return ALL_ICONS.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose an icon" size="lg">
      <div className="space-y-6">
        {/* Search & Filter */}
        <div className="flex flex-col gap-4">
          <TextInput
            placeholder={t('iconPicker.searchPlaceholder', 'Search icons...')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            icon={Search}
            className="!mt-0"
          />

          <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                }}
                className={`px-3 py-1.5 eyebrow rounded-lg transition-all ${
                  activeCategory === cat.id ? 'bg-white text-slate-950 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Icons Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3 max-h-[400px] overflow-y-auto p-1 custom-scrollbar">
          {filteredIcons.map((item) => {
            const Icon = item.icon;
            const isSelected = selectedIcon === item.name;
            return (
              <button
                key={item.name}
                onClick={() => {
                  onSelect(item.name);
                  onClose();
                }}
                className={`group flex flex-col items-center justify-center gap-2 aspect-square rounded-2xl border transition-all ${
                  isSelected
                    ? 'border-lime-400 bg-lime-400/10 text-lime-900 shadow-md ring-2 ring-lime-400/20'
                    : 'border-gray-100 bg-white hover:border-lime-400/30 hover:bg-gray-50 text-gray-400 hover:text-gray-600'
                }`}
                title={item.name}
              >
                <Icon
                  className={`h-6 w-6 transition-transform group-hover:scale-110 ${isSelected ? 'text-lime-900' : ''}`}
                />
                <span className="text-[8px] font-bold truncate w-full px-1 text-center uppercase tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.name}
                </span>
              </button>
            );
          })}

          {filteredIcons.length === 0 && (
            <div className="col-span-full py-12 text-center bg-gray-50 rounded-3xl border-2 border-dashed border-gray-100">
              <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
              <p className="nav-label-inactive">No icons found for &quot;{search}&quot;</p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} title="Cancel" />
        </div>
      </div>
    </Modal>
  );
}
